import React, { useEffect, useState } from 'react';
import { FormController, getParamsForm, useOneFormContext } from 'src/feature-app';
import { OneElementTemplateFullScreen } from 'src/feature-app/Screen';
import { useDispatch, useSelector, useStore } from 'react-redux';
import { FormDataGroup, FormInfo, OneFormState, Steps } from 'src/types';
import { Text } from '@volkswagen-onehub/components-core';
import { CarlineSelect, MultipleCTASelection } from 'src/components';
import { ConfirmacionNoCompra, ThankYouNoCompra, MapaNoCompra, PantallaOtrosModelos } from '.';
import { NoCompraFormProps } from './NoCompraController';
import { formDataBuilder } from '../format-fields';
import { dpsSend } from 'src/bff';
import { useInitializeNoCompraForm } from './use-initialize-no-compra-form';
import { addRemovedStep, getIndexInSteps, removeStep, resetStepsScreenIndex } from '../helpers/add-remove-steps';
import { CTAsSiNo } from 'src/feature-app/InputsValues/CTAsObjects';

const getModeloInteresTitle = (quieroVolkswagen: string) => {
  if (quieroVolkswagen === 'true') {
    return (
      <>
        <Text bold>¿Qué modelo </Text>en{' '}
        <span style={{ whiteSpace: 'nowrap' }}>
          concreto?
          <sup style={{ top: 'auto', fontSize: '100%' }}>*</sup>
        </span>
      </>
    );
  } else {
    return (
      <>
        <Text bold>¿Qué otros modelos </Text>te interesan?
      </>
    );
  }
};

const getModeloInteresFields = (quieroVolkswagen: string) => {
  if (quieroVolkswagen === 'true') {
    return (
      <OneElementTemplateFullScreen
        element={
          <div style={{ marginBottom: '2px' }}>
            <CarlineSelect useCarline={true} notALayer={true} />
          </div>
        }
      />
    );
  } else {
    return <OneElementTemplateFullScreen allowOverflowingContent element={<PantallaOtrosModelos />} />;
  }
};

const getModeloInteresOutputs = (quieroVolkswagen: string) => {
  if (quieroVolkswagen === 'true') {
    return ['modelo'];
  } else {
    return ['modeloUno', 'modeloDos'];
  }
};
const getModeloInteresOutputsText = (quieroVolkswagen: string) => {
  if (quieroVolkswagen === 'true') {
    return ['modelo'];
  } else {
    return ['modeloUno', ' / ', 'modeloDos'];
  }
};

export enum SigueBuscandoFormSteps {
  QuieroVolkswagen,
  Modelo,
  MasInformacion,
  Mapa,
  Confirmacion,
}

export function NoCompraNoTengo({ dpsData, idLead, idPerson, trackFormLoad, estado }: NoCompraFormProps) {
  const { initializeForm, handleNextStep } = useOneFormContext();
  const { formData, formInfo } = useSelector((state: OneFormState) => state);
  const { quieroVolkswagen, quieroContacto } = formData.fields;
  const { screenIndex } = formInfo;
  const dispatch = useDispatch();
  const [loadingPresubmit, setLoadingPresubmit] = useState(true);
  const store = useStore();

  useEffect(() => {
    const sendPresubmit = async () => {
      const result = await sendForm(true);

      if (!result) {
        // handleError();
      }
      setLoadingPresubmit(false);
    };

    sendPresubmit();
  }, []);

  const updateModeloInteresStep = () => {
    const { quieroVolkswagen } = store.getState().formData.fields;
    const { steps: actualSteps } = store.getState().formInfo;
    const title = getModeloInteresTitle(quieroVolkswagen);
    const fields = getModeloInteresFields(quieroVolkswagen);
    const outputs = getModeloInteresOutputs(quieroVolkswagen);
    const outputsText = getModeloInteresOutputsText(quieroVolkswagen);

    let newSteps = [...steps];

    const modeloIndex = newSteps.findIndex((step: Steps) => step.name === 'Modelo');

    newSteps[modeloIndex].title = title;
    newSteps[modeloIndex].fields = fields;
    newSteps[modeloIndex].outputs = outputs;
    newSteps[modeloIndex].outputsText = outputsText;

    if (quieroVolkswagen === 'false') {
      newSteps = removeStep(newSteps, 'Mapa');
      dispatch({ type: 'UPDATE_FIELDS', payload: { quieroContacto: 'false' } });
    } else {
      const mapaIndex = getIndexInSteps(newSteps, 'Mapa');
      if (mapaIndex === -1) {
        newSteps = addRemovedStep(steps, actualSteps, 'Mapa', screenIndex);
      }
    }

    newSteps = resetStepsScreenIndex(newSteps);

    dispatch({ type: 'UPDATE_STEPS', payload: newSteps });
    dispatch({ type: 'UPDATE_LAST_STEP', payload: newSteps[newSteps.length - 1] });
    dispatch({ type: 'SET_NUMBER_OF_SCREENS', payload: newSteps.length });
  };

  const steps: Steps[] = [
    {
      title: (
        <>
          Nos encantaría saber más.{' '}
          <Text bold>
            ¿Aún te interesa conducir un{' '}
            <span style={{ whiteSpace: 'nowrap' }}>
              Volkswagen?<sup style={{ top: 'auto', fontSize: '100%' }}>*</sup>
            </span>
          </Text>
        </>
      ),
      fields: (
        <OneElementTemplateFullScreen
          element={<MultipleCTASelection ctaValues={CTAsSiNo} name="quieroVolkswagen" ctaWidth="81" nextOnClick />}
        />
      ),
      screenIndex: SigueBuscandoFormSteps.QuieroVolkswagen,
      name: 'quieroVolkswagen',
      outputs: ['quieroVolkswagen'],
      outputsText: ['quieroVolkswagen'],
      trueOrFalseQuestion: true,
    },
    {
      title: <></>, // Se actualiza en updateModeloInteresStep
      fields: <></>, // Se actualiza en updateModeloInteresStep
      screenIndex: SigueBuscandoFormSteps.Modelo,
      name: 'Modelo',
      showOverflow: true,
    },
    {
      screenIndex: SigueBuscandoFormSteps.Mapa,
      name: 'Mapa',
      multiStep: true,
      component: <MapaNoCompra formName="venta" showHorario={false} />,
    },
    {
      fields: <ConfirmacionNoCompra idPerson={idPerson} />,
      screenIndex: SigueBuscandoFormSteps.Confirmacion,
      name: 'Confirmacion',
      outputs: ['confirmacion'],
      outputsText: ['confirmacion'],
    },
  ];

  const NuevoFormData: Partial<FormDataGroup> = {
    headers: {
      DESC_BRAND: 'VW',
      DESC_VN_VO: 'VN',
      DESC_GDPR_ADAPTED: 'true',
    },
    fields: { formName: 'no_compra-no_tengo', formType: 'otherform', pageCategory: 'Encuesta' }, // Analítica
  };

  const generateData = (data?: Partial<FormDataGroup>) => {
    const { formData, dealersInfo }: OneFormState = store.getState();
    const { fields, headers } = formData;
    const { selectedInstallation } = dealersInfo;

    let TXT_V_KVPS_code__c, FRML_TXT_codVentaProvincia__c;
    if (selectedInstallation) {
      TXT_V_KVPS_code__c = selectedInstallation.TXT_V_KVPS_code__c;
      FRML_TXT_codVentaProvincia__c = selectedInstallation.FRML_TXT_codVentaProvincia__c;
    }
    // let {
    //   TXT_V_Installation_Data_Name__c,
    //   TXT_V_KVPS_code__c,
    //   FRML_TXT_codVentaProvincia__c,
    //   TXT_V_Installation_Code__c,
    // } = selectedInstallation;
    const { tc, today, url, device } = getParamsForm();
    return {
      'headers[TC]': tc,
      'headers[PINCODE]': dpsData.pincode.pincodeResponse.params.pincode,
      'headers[FECHA_INSERTION_DATE]': today,
      'headers[DESC_VN_VO]': 'VN',

      'headers[DESC_TOKEN]': 'VW_DDB_FORM_NOCOMPRA', // CAMBIA PRESUMBIT/SUBMIT
      'headers[DESC_ID_CAMPAIGN]': '7011n000000Aj7uAAC',
      'headers[DESC_FORM_DETAIL]': 'NoCompra Grabación', // CAMBIA PRESUMBIT/SUBMIT
      'headers[DESC_FORMOBJECTIVE]': fields.quieroContacto === 'true' ? 'Más Información' : 'Encuesta',
      'headers[DESC_DEVICETYPE]': device,
      'headers[DESC_CAMPAIGNBLOCK]': '9. VW LEM',
      'headers[DESC_BRAND]': 'VW',
      'headers[COD_FORM_ID]': fields.formName ? `vw-oneform-${fields.formName}` : `vw-oneform-${data.fields.formName}`,
      'headers[DESC_URLORIGIN]': url,

      'fields[NUM_CELLPHONE]': fields.quieroContacto === 'true' ? idPerson.NUM_Phone_mobile__c : null,
      'fields[DESC_SURNAME_2]': fields.quieroContacto === 'true' ? idPerson.TXT_2_Surname__c : null,
      'fields[DESC_SURNAME_1]': fields.quieroContacto === 'true' ? idPerson.LastName : null,
      'fields[DESC_NAME]': fields.quieroContacto === 'true' ? idPerson.FirstName : null,
      'fields[DESC_LOPD]': 'PERMITE', // SOLO EN SUBMIT
      'fields[DESC_TRANF_DATA_THIRD]': 'NO INFORMADO', // SOLO EN SUBMIT
      'fields[DESC_LSSI]': fields.lssi ? fields.lssi : 'NO INFORMADO',
      'fields[DESC_LOPD_GDPR_PERFILADO]': fields.tmk ? fields.tmk : 'NO INFORMADO',
      'fields[DESC_FORM_VERSION]': fields.formName
        ? `vw-oneform-${fields.formName}-${process.env.VERSION}`
        : `vw-oneform-${data.fields.formName}-${process.env.VERSION}`,
      'fields[DESC_EMAIL_ADDRESS]': fields.quieroContacto === 'true' ? idPerson.TXT_Email__c : null,
      'fields[DESC_DEALER_SHARE]': fields.compartir, // SOLO EN SUBMIT
      'fields[DESC_DEALER_CODE]': idLead.TXT_DESC_DEALER_CODE__c,
      'fields[DESC_DEALER]': idLead.DESC_Dealer_Code__c,
      'fields[DESC_AUXFIELD1]': 'No tengo',
      'fields[DESC_AUXFIELD4]': fields.quieroVolkswagen === 'true' ? null : fields.modeloUno ? fields.modeloUno : null,
      'fields[DESC_AUXFIELD5]': fields.quieroVolkswagen === 'true' ? null : fields.modeloDos ? fields.modeloDos : null,
      'fields[COD_INTEREST_CARLINE]': fields.carlineCode,
      'fields[DESC_INTEREST_CARLINE]': fields.modelo,
      'fields[DESC_AUXFIELD20]': idLead.External_Id_Climapro__c,
      'fields[DESC_DEALER_PROVINCE]': fields.provinceName,
      'fields[COD_DEALER_PROVINCE]': FRML_TXT_codVentaProvincia__c,
      'fields[DESC_DEALER_CODE_KVPS]': TXT_V_KVPS_code__c,
      'fields[COD_LEAD_ID]': idLead.Id,
      'fields[DESC_DATA_ENRICHMENT]': 'NO INFORMADO',

      'flags[FLAG_ISLEAD]': fields.quieroVolkswagen === 'false' ? 'false' : fields.quieroContacto,
      'flags[FLAG_ISPROSPECT]':
        fields.quieroVolkswagen === 'true' && fields.quieroContacto === 'false' ? 'true' : 'false',
      'flags[FLAG_GENERATELEM]': fields.quieroVolkswagen === 'false' ? 'false' : fields.quieroContacto,
    };
  };

  const generateSubmitData = () => generateData();

  /**
   * Generamos la misma data que para el submit y eliminamos todos los campos que no son necesarios para el presubmit.
   */
  const generatePresubmitData = () => {
    const data = generateData(NuevoFormData);

    delete data['headers[PINCODE]'];

    //
    delete data['headers[DESC_VN_VO]'];
    delete data['fields[DESC_TRANF_DATA_THIRD]'];
    //

    delete data['flags[FLAG_ISLEAD]'];
    delete data['flags[FLAG_ISPROSPECT]'];
    delete data['flags[FLAG_GENERATELEM]'];
    delete data['fields[DESC_DEALER_SHARE]'];
    delete data['fields[DESC_NAME]'];
    delete data['fields[DESC_SURNAME_1]'];
    delete data['fields[DESC_SURNAME_2]'];
    delete data['fields[DESC_EMAIL_ADDRESS]'];
    delete data['fields[DESC_AUXFIELD4]'];
    delete data['fields[DESC_AUXFIELD5]'];
    delete data['fields[DESC_AUXFIELD18]'];
    delete data['fields[DESC_AUXFIELD20]'];
    delete data['fields[NUM_CELLPHONE]'];
    delete data['fields[DESC_LOPD]'];
    delete data['fields[DESC_LSSI]'];
    delete data['fields[DESC_LOPD_GDPR_PERFILADO]'];
    delete data['fields[M_OBSERVATION]'];
    delete data['fields[DESC_DATA_ENRICHMENT]'];

    data['fields[DESC_AUXFIELD1]'] = 'No tengo';
    data['headers[DESC_FORM_DETAIL]'] = 'NoCompra Pregrabación';
    data['headers[DESC_TOKEN]'] = 'VW_DDB_FORM_NOCOMPRA_PRE';

    return data;
  };

  const sendForm = async (isPresubmit?: boolean): Promise<boolean> => {
    return new Promise<any>(async (resolve, reject) => {
      const data = isPresubmit ? generatePresubmitData() : generateSubmitData();
      const dataStringified = JSON.stringify(data);
      const dataParsed = JSON.parse(dataStringified);

      const formData = formDataBuilder(dataParsed);

      const response = await dpsSend(formData);

      if (response.data?.content?.code === '200') {
        dispatch({ type: 'SET_FORM_RESPONSE', payload: response.data });
        resolve(true);
      } else {
        resolve(false);
      }
    });
  };

  const { firstStep, lastStep, loading } = useInitializeNoCompraForm(steps);

  useEffect(() => {
    const NuevoFormInfo: Partial<FormInfo> = {
      formStarted: true,
      sendFormCallback: sendForm,
      fullScreen: firstStep.fullScreen ? true : false,
      lastStep,
      numberOfScreens: steps.length,
      screenIndex: firstStep.screenIndex,
      formTheme: 'main',
      notALayer: true,
      formType: 'inPage',
      step: firstStep,
      steps,
      stepsHistory: [0],
      thankYouComponent: <ThankYouNoCompra />,
    };

    if (!loading) {
      initializeForm(NuevoFormInfo, NuevoFormData, true);
      trackFormLoad();
    }
  }, [loading]);

  //activación de lógica para la gestion de steps

  useEffect(() => {
    if (quieroVolkswagen) updateModeloInteresStep();
  }, [quieroVolkswagen]);


  return loading || loadingPresubmit || !steps ? null : <FormController steps={steps} screenType="full-screen" />;
}
